import React from "react";
import styles from "./PricingCard.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faCrown, faStar} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

export default function PricingCard({plan, currentSubscription, onSelect, disabled, billingCycle, pricing}) {
    const {t} = useTranslation();
    const isCurrentPlan = currentSubscription?.payment_plan?.id === plan.id;

    // Support both old format (string with \n) and new format (array of objects)
    const getPerks = () => {
        if (Array.isArray(plan.perks)) {
            return plan.perks.sort((a, b) => (a.order || 0) - (b.order || 0)).map(p => p.text);
        }
        if (typeof plan.perks === 'string') {
            return plan.perks.split('\n').filter(p => p.trim());
        }
        return [];
    };

    const perks = getPerks();

    const formatValue = (value) => {
        return value === 0 ? t('pricing.unlimited') : value;
    };

    const getDisplayPrice = () => {
        if (plan.is_free || plan.price === 0) return 0;
        if (billingCycle === 'yearly' && pricing) {
            return (pricing.finalPrice / 12).toFixed(0); // Monthly equivalent
        }
        return plan.price.toFixed(0);
    };

    const showSavings = billingCycle === 'yearly' && pricing?.savings > 0 && !plan.is_free;

    const getCardClass = () => {
        let classes = [styles.card];
        if (plan.is_free) classes.push(styles.free);
        if (isCurrentPlan) classes.push(styles.current);
        if (plan.is_popular) classes.push(styles.popular);
        if (plan.is_premium) classes.push(styles.enterprise);
        return classes.join(' ');
    };

    const getButtonText = () => {
        if (isCurrentPlan) return t('pricing.currentPlan');
        if (plan.is_free) return t('pricing.startFree');
        return t('pricing.selectPlan');
    };

    const isUpgrade = () => {
        if (!currentSubscription?.payment_plan) return true;
        return plan.order > currentSubscription.payment_plan.order;
    };

    return (
        <div className={getCardClass()}>
            {/* Show badge based on is_popular or is_premium flags */}
            {plan.is_popular && !isCurrentPlan && (
                <div className={styles.popularBadge}>
                    <FontAwesomeIcon icon={faStar} /> {t('pricing.mostPopular')}
                </div>
            )}

            {plan.is_premium && !plan.is_popular && !isCurrentPlan && (
                <div className={styles.enterpriseBadge}>
                    <FontAwesomeIcon icon={faCrown} /> {t('pricing.premium')}
                </div>
            )}

            {isCurrentPlan && (
                <div className={styles.currentBadge}>
                    {t('pricing.yourPlan')}
                </div>
            )}

            <div className={styles.header}>
                <h3 className={styles.planName}>{plan.name}</h3>
                <p className={styles.description}>{plan.description}</p>
            </div>

            <div className={styles.priceSection}>
                {showSavings && (
                    <span className={styles.originalPrice}>${plan.price.toFixed(0)}</span>
                )}
                <div className={styles.priceRow}>
                    <span className={styles.currency}>$</span>
                    <span className={styles.price}>{getDisplayPrice()}</span>
                    <span className={styles.period}>{t('pricing.perMonth')}</span>
                </div>
                {showSavings && (
                    <div className={styles.savingsBadge}>
                        {t('pricing.savingsLabel', {savings: pricing.savings.toFixed(0), discount: pricing.discountApplied})}
                    </div>
                )}
                {billingCycle === 'yearly' && !plan.is_free && (
                    <div className={styles.billedAnnually}>
                        {t('pricing.billedAnnually', {price: pricing?.finalPrice.toFixed(2)})}
                    </div>
                )}
            </div>

            <div className={styles.features}>
                <div className={styles.featureItem}>
                    <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                    <span><strong>{formatValue(plan.max_devices)}</strong> {t('pricing.devices', {count: plan.max_devices === 0 ? 2 : plan.max_devices})}</span>
                </div>
                <div className={styles.featureItem}>
                    <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                    <span><strong>{formatValue(plan.max_users)}</strong> {t('pricing.users', {count: plan.max_users === 0 ? 2 : plan.max_users})}</span>
                </div>
                <div className={styles.featureItem}>
                    <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                    <span><strong>{formatValue(plan.max_companies)}</strong> {t('pricing.companies', {count: plan.max_companies === 0 ? 2 : plan.max_companies})}</span>
                </div>
                <div className={styles.featureItem}>
                    <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                    <span>{t('pricing.refreshRate', {seconds: plan.refresh_rate_seconds})}</span>
                </div>

                {perks.map((perk, index) => (
                    <div key={index} className={styles.featureItem}>
                        <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
                        <span>{perk}</span>
                    </div>
                ))}
            </div>

            <button
                className={`${styles.button} ${isCurrentPlan ? styles.currentButton : ''} ${!isCurrentPlan && !plan.is_free ? styles.primaryButton : ''} ${plan.is_free && !isCurrentPlan ? styles.freeButton : ''}`}
                onClick={() => !isCurrentPlan && !disabled && onSelect(plan)}
                disabled={isCurrentPlan || disabled}
            >
                {getButtonText()}
            </button>

            {!isCurrentPlan && currentSubscription && isUpgrade() && (
                <p className={styles.upgradeText}>{t('pricing.upgradeYourPlan')}</p>
            )}
        </div>
    );
}
